import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Phone, Mail, User, MapPin, Calendar, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

export default function AdminLeads() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const limit = 20;

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLeads = useCallback((nextPage, nextSearch, nextStatusFilter) => {
    setLoading(true); setLoadError("");
    const params = { skip: nextPage * limit, limit };
    if (nextSearch) params.search = nextSearch;
    if (nextStatusFilter !== "all") params.status = nextStatusFilter;
    axios.get(`${API}/admin/leads`, { headers, params })
      .then(r => { setLeads(r.data.leads); setTotal(r.data.total); setLoading(false); })
      .catch(() => {setLoadError("Leads could not be loaded. Existing results may be out of date."); setLoading(false);});
  }, [headers, limit]);

  useEffect(() => { fetchLeads(page, search, statusFilter); }, [page, search, statusFilter, fetchLeads]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLeads(0, search, statusFilter);
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { status }, { headers });
      toast.success("Lead status updated");
      fetchLeads(page, search, statusFilter);
      if (selectedLead?.id === leadId) setSelectedLead(prev => ({ ...prev, status }));
    } catch { toast.error("Failed to update status"); }
  };

  const updateNotes = async (leadId, notes) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}`, { notes }, { headers });
      toast.success("Notes saved");
    } catch { toast.error("Failed to save notes"); }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers });
      toast.success("Lead deleted");
      setSelectedLead(null);
      fetchLeads(page, search, statusFilter);
    } catch { toast.error("Failed to delete"); }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    quoted: "bg-orange-100 text-orange-700",
    won: "bg-green-100 text-green-700",
    lost: "bg-gray-100 text-gray-600"
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Leads</h1>
        <p className="text-sm text-[#4B5563]">View and manage all quote requests</p>
      </div>

      {loadError && <div role="alert"><p>{loadError}</p><Button onClick={()=>fetchLeads(page,search,statusFilter)}>Retry loading leads</Button></div>}
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="pl-9"
              data-testid="admin-leads-search-input"
            />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#4B5563]">{loadError ? "Lead count unavailable" : `${total} lead${total !== 1 ? "s" : ""} found`}</p>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}</div>
      ) : loadError && leads.length === 0 ? <p>Lead results unavailable.</p> : leads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#4B5563]">No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-100">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="hidden md:table-cell">Service</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedLead(lead)}
                    data-testid="admin-leads-row"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#0D0D0D]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-[#0D0D0D]">{lead.full_name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <span className="font-medium text-sm">{lead.full_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-xs text-[#4B5563]">{lead.email}</p>
                      <p className="text-xs text-[#4B5563]">{lead.phone}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs">{lead.service_needed}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${statusColors[lead.status] || statusColors.new}`}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-xs text-[#4B5563]">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#4B5563]">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0D0D0D]/10 rounded-full flex items-center justify-center">
                  <span className="text-lg font-semibold text-[#0D0D0D]">{selectedLead.full_name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-semibold text-[#0D0D0D]">{selectedLead.full_name}</p>
                  <Badge className={`text-xs ${statusColors[selectedLead.status] || statusColors.new}`}>{selectedLead.status}</Badge>
                </div>
              </div>

              <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-2 text-sm text-[#0D0D0D] hover:underline">
                  <Mail className="w-4 h-4" /> {selectedLead.email}
                </a>
                <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-2 text-sm text-[#0D0D0D] hover:underline">
                  <Phone className="w-4 h-4" /> {selectedLead.phone}
                </a>
                <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                  <Calendar className="w-4 h-4" /> {new Date(selectedLead.created_at).toLocaleString()}
                </div>
              </div>

              <div>
                <p className="text-xs text-[#4B5563] font-medium mb-1">Service Requested</p>
                <Badge variant="outline">{selectedLead.service_needed}</Badge>
              </div>

              <div>
                <p className="text-xs text-[#4B5563] font-medium mb-1">Message</p>
                <p className="text-sm text-[#1F2937] bg-gray-50 rounded-lg p-3">{selectedLead.message}</p>
              </div>

              <div>
                <p className="text-xs text-[#4B5563] font-medium mb-2">Status Pipeline</p>
                <div className="flex gap-2 flex-wrap">
                  {["new", "contacted", "quoted", "won", "lost"].map(s => (
                    <Button
                      key={s}
                      size="sm"
                      variant={selectedLead.status === s ? "default" : "outline"}
                      className={selectedLead.status === s ? "bg-[#0D0D0D] text-white" : ""}
                      onClick={() => updateStatus(selectedLead.id, s)}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs text-[#4B5563] font-medium mb-1">Admin Notes</p>
                <textarea
                  className="w-full text-sm border border-gray-200 rounded-lg p-3 min-h-[60px] resize-y"
                  defaultValue={selectedLead.notes || ""}
                  placeholder="Add private notes about this lead..."
                  onBlur={(e) => updateNotes(selectedLead.id, e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <a href={`tel:${selectedLead.phone}`} className="flex-1">
                  <Button className="w-full bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] gap-2">
                    <Phone className="w-4 h-4" /> Call
                  </Button>
                </a>
                <a href={`mailto:${selectedLead.email}`} className="flex-1">
                  <Button variant="outline" className="w-full gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </Button>
                </a>
              </div>

              <Button variant="ghost" className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => deleteLead(selectedLead.id)}>
                Delete This Lead
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
